import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ShoppingBag, User, Menu, X, Heart } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useCartStore } from '../../store/cartStore';
import CurrencySelector from '../ui/CurrencySelector';
import { useCurrencyStore } from '../../store/currencyStore';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { isAuthenticated, user } = useAuthStore();
  const { cart, fetchCart, toggleCart } = useCartStore();
  const { fetchExchangeRates } = useCurrencyStore();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchCart();
  }, [isAuthenticated]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  useEffect(() => {
    fetchExchangeRates();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const navLinks = [
    { label: 'New Arrivals', href: '/shop?sort=newest' },
    { label: 'Collections', href: '/collections' },
    { label: 'Men', href: '/shop?category=men' },
    { label: 'Women', href: '/shop?category=women' },
    { label: 'Sale', href: '/shop?sale=true' },
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <>
      {/* Announcement Bar */}
      <div className="bg-primary text-white text-center py-2">
        <p className="text-[11px] sm:text-xs tracking-wide sm:tracking-widest uppercase">
          Complimentary shipping on orders over $300
        </p>
      </div>

      {/* Main Navbar */}
      <nav className={`sticky top-0 z-50 bg-white transition-all duration-300 ${
        isScrolled ? 'shadow-sm' : ''
      }`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20 sm:h-16">

            {/* Left - Nav Links (Desktop) */}
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.slice(0, 2).map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`text-xs tracking-widest uppercase transition-colors duration-300 ${
                    location.pathname + location.search === link.href
                      ? 'text-primary border-b border-primary'
                      : 'text-primary hover:text-nude'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen
                ? <X size={24} className="text-primary" />
                : <Menu size={24} className="text-primary" />
              }
            </button>

{/* Center - Logo */}
<Link
  to="/"
  className="absolute left-1/2 transform -translate-x-1/2"
>
  <img
    src="/logo.png"
    alt="Lanre Savage"
    className="h-14 sm:h-16 md:h-24 w-auto object-contain"
  />
</Link>

            {/* Right - Icons */}
            <div className="flex items-center space-x-3 sm:space-x-5">
              <div className="hidden lg:flex">
                <CurrencySelector />
              </div>
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="text-primary hover:text-nude transition-colors"
              >
                <Search size={20} className="sm:w-[18px] sm:h-[18px]" />
              </button>

              {isAuthenticated && (
                <Link
                  to="/wishlist"
                  className="text-primary hover:text-nude transition-colors hidden lg:block"
                >
                  <Heart size={18} />
                </Link>
              )}

              <Link
                to={isAuthenticated ? '/account' : '/login'}
                className="text-primary hover:text-nude transition-colors"
              >
                <User size={20} className="sm:w-[18px] sm:h-[18px]" />
              </Link>

              <button
                onClick={toggleCart}
                className="text-primary hover:text-nude transition-colors relative"
              >
                <ShoppingBag size={20} className="sm:w-[18px] sm:h-[18px]" />
                {cart && cart.item_count > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary text-white text-[10px] sm:text-xs w-4 h-4 rounded-full flex items-center justify-center">
                    {cart.item_count}
                  </span>
                )}
              </button>

              {user?.role === 'admin' || user?.role === 'super_admin' ? (
                <Link
                  to="/admin"
                  className="hidden lg:block text-xs tracking-widest uppercase text-nude hover:text-primary transition-colors"
                >
                  Admin
                </Link>
              ) : null}
            </div>
          </div>

          {/* Desktop Secondary Nav */}
          <div className="hidden lg:flex items-center justify-center space-x-8 pb-3 border-t border-light/50 pt-3">
            {navLinks.slice(2).map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className={`text-xs tracking-widest uppercase transition-colors duration-300 ${
                  location.pathname === link.href
                    ? 'text-primary border-b border-primary pb-1'
                    : 'text-primary hover:text-nude'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Search Bar */}
        <AnimatePresence>
          {isSearchOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="border-t border-light overflow-hidden"
            >
              <form onSubmit={handleSearch} className="max-w-2xl mx-auto px-6 py-4">
                <div className="flex items-center space-x-4">
                  <Search size={16} className="text-nude flex-shrink-0" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="SEARCH"
                    autoFocus
                    className="flex-1 bg-transparent text-sm tracking-widest uppercase outline-none placeholder-nude"
                  />
                  <button type="button" onClick={() => setIsSearchOpen(false)}>
                    <X size={16} className="text-nude hover:text-primary transition-colors" />
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed inset-y-0 left-0 z-50 w-80 bg-white shadow-xl"
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-6 py-5 border-b border-light">
                <span className="font-serif text-lg tracking-widest uppercase">Menu</span>
                <button onClick={() => setIsMenuOpen(false)}>
                  <X size={20} className="text-primary" />
                </button>
              </div>

              <nav className="flex-1 px-6 py-8 space-y-6 overflow-y-auto">
                {navLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.href}
                    className={`block text-sm tracking-widest uppercase transition-colors ${
                      location.pathname === link.href
                        ? 'text-primary font-medium'
                        : 'text-secondary hover:text-primary'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="border-t border-light pt-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs tracking-widest uppercase text-nude">Currency</span>
                    <CurrencySelector />
                  </div>
                  {isAuthenticated ? (
                    <>
                      <Link to="/account" className="block text-sm tracking-widest uppercase text-primary hover:text-nude transition-colors">
                        My Account
                      </Link>
                      <Link to="/wishlist" className="block text-sm tracking-widest uppercase text-primary hover:text-nude transition-colors">
                        Wishlist
                      </Link>
                      <Link to="/orders" className="block text-sm tracking-widest uppercase text-primary hover:text-nude transition-colors">
                        My Orders
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link to="/login" className="block text-sm tracking-widest uppercase text-primary hover:text-nude transition-colors">
                        Login
                      </Link>
                      <Link to="/register" className="block text-sm tracking-widest uppercase text-primary hover:text-nude transition-colors">
                        Create Account
                      </Link>
                    </>
                  )}
                </div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="overlay lg:hidden"
            onClick={() => setIsMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;